import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { apiGetPrivateTours, shouldShowOnHomepage, type PrivateTour } from '../../api/privateTours/privateTourApi'
import { Link } from 'react-router-dom'
import { ArrowRight, Bookmark, Camera, CarFront } from 'lucide-react'
import useSavedPrivateTours from '../../hooks/useSavedPrivateTours'






function TourCard({
  id,
  title,
  subtitle,
  duration,
  price,
  image,
  isSaved,
  onSaveToggle,
}: {
  id: string | number
  title: string
  subtitle?: string
  duration: string
  price: string | number
  image: string
  isSaved: boolean
  onSaveToggle: () => void
}) {

  return (
    <article className="relative w-[280px] sm:w-[304px] min-h-[500px] shrink-0 snap-center overflow-hidden rounded-[14px] bg-[#efeeee] shadow-[0_2px_7px_rgba(103,89,89,0.25)] flex flex-col">
      <div className="absolute left-[16px] top-[12px] z-10">
        <span className="font-host text-[15px] font-semibold leading-none tracking-[-0.01em] text-[#c56c3d]">
          {duration}
        </span>
      </div>

      <div
        className="relative h-[336px] shrink-0 overflow-hidden"
        style={{
          clipPath: `polygon(304px 454px, 304px 454px, 303.803668px 456.433012px, 303.235264px 458.741056px, 302.325676px 460.893244px, 301.105792px 462.858688px, 299.6065px 464.6065px, 297.858688px 466.105792px, 295.893244px 467.325676px, 293.741056px 468.235264px, 291.433012px 468.803668px, 289px 469px, 15px 469px, 15px 469px, 12.56692239px 468.803668px, 10.25884032px 468.235264px, 8.10663693px 467.325676px, 6.14119536px 466.105792px, 4.39339875px 464.6065px, 2.89413024px 462.858688px, 1.67427297px 460.893244px, 0.76471008px 458.741056px, 0.19632471px 456.433012px, 2.4833328970406E-31px 454px, 0px 55px, 0px 55px, 0.19632471px 52.5669151px, 0.76471008px 50.2588288px, 1.67427297px 48.1066237px, 2.89413024px 46.1411824px, 4.39339875px 44.3933875px, 6.14119536px 42.8941216px, 8.10663693px 41.6742673px, 10.25884032px 40.7647072px, 12.56692239px 40.1963239px, 15px 40px, 129px 40px, 129px 40px, 131.433012px 39.8036761px, 133.741056px 39.2352928px, 135.893244px 38.3257327px, 137.858688px 37.1058784px, 139.6065px 35.6066125px, 141.105792px 33.8588176px, 142.325676px 31.8933763px, 143.235264px 29.7411712px, 143.803668px 27.4330849px, 144px 25px, 144px 15px, 144px 15px, 144.196332px 12.56692239px, 144.764736px 10.25884032px, 145.674324px 8.10663693px, 146.894208px 6.14119536px, 148.3935px 4.39339875px, 150.141312px 2.89413024px, 152.106756px 1.67427297px, 154.258944px 0.76471008px, 156.566988px 0.19632471px, 159px 2.4833328970406E-31px, 289px 0px, 289px 0px, 291.433012px 0.19632471px, 293.741056px 0.76471008px, 295.893244px 1.67427297px, 297.858688px 2.89413024px, 299.6065px 4.39339875px, 301.105792px 6.14119536px, 302.325676px 8.10663693px, 303.235264px 10.25884032px, 303.803668px 12.56692239px, 304px 15px, 304px 454px)`,
        }}
      >
        <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

       <button
          type="button"
          aria-label={isSaved ? 'Remove saved tour' : 'Save tour'}
          aria-pressed={isSaved}
          onClick={onSaveToggle}
          className="absolute right-2.5 top-2.5 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/95 shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <Bookmark
            className={`h-[15px] w-[15px] stroke-[2.1] transition-colors duration-200 ${
              isSaved
                ? 'fill-[#EF4444] stroke-[#EF4444] text-[#EF4444]'
                : 'fill-[#8b8b8b] stroke-[#8b8b8b] text-[#8b8b8b]'
            }`}
          />
        </button>
      </div>

      <div className="flex-1 bg-white px-[12px] pb-[12px] pt-[10px] flex flex-col">
        <div>
          <div className="mb-2.5 flex items-center gap-2 text-[12px] leading-none text-[#202020]">
            <span className="inline-flex items-center gap-1">
              <Camera className="h-3 w-3 fill-current stroke-[2.1]" />
              Sightseeing
            </span>
            <span className="text-[#c26f40]">•</span>
            <span className="inline-flex items-center gap-1">
              <CarFront className="h-3 w-3 fill-current stroke-[2.1]" />
              Transfers
            </span>
          </div>

          <h3 className="font-host text-[15px] font-semibold leading-[1.23] tracking-[-0.02em] text-[#111111]">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 font-host text-[13px] font-medium leading-snug text-slate-500 line-clamp-2">
              {subtitle}
            </p>
          )}
        </div>

        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <p className="font-host text-[12px] leading-none text-[#666666]">Starting From</p>
            <div className="mt-1 flex items-end gap-1">
              <span className="font-host text-[19px] font-medium leading-none tracking-[-0.03em] text-[#555555]">
                €{price}
              </span>
            </div>
          </div>

          <Link
            to={`/private-tours?id=${id}`}
            className="flex h-[42px] items-center justify-center rounded-[15px] bg-[#ff7a29] px-[24px] font-host text-[13px] font-semibold tracking-[-0.01em] text-white border border-[#B8B8B8] shadow-[0_2px_4px_rgba(0,0,0,0.12)] transition-all hover:bg-[#e66c22] active:scale-95"
          >
            Explore Trip
          </Link>
        </div>
      </div>
    </article>
  )
}

export default function VanTours() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [vanTours, setVanTours] = useState<PrivateTour[]>([])
  const { savedIds, toggleSaved } = useSavedPrivateTours()

  useEffect(() => {
    async function loadTours() {
      const res = await apiGetPrivateTours()
      if (res.data && res.data.length > 0) {
        const filtered = res.data.filter(shouldShowOnHomepage)
        setVanTours(filtered.length > 0 ? filtered : res.data)
      }
    }
    loadTours()
  }, [])

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>

    const handleScroll = () => {
      if (window.innerWidth >= 768) return
      
      const el = scrollRef.current
      if (!el) return

      interval = setInterval(() => {
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
          el.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
          el.scrollBy({ left: 320, behavior: 'smooth' })
        }
      }, 3000)
    }

    handleScroll()
    window.addEventListener('resize', () => {
      clearInterval(interval)
      handleScroll()
    })

    return () => clearInterval(interval)
  }, [])

  const scroll = () => {
    scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' })
  }

  if (vanTours.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[linear-gradient(90deg,#f7f7f3_0%,#edf4ef_38%,#d6edf4_100%)] px-4 py-8 sm:px-6 lg:px-[35px] lg:py-[28px]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.75),transparent_32%),radial-gradient(circle_at_90%_15%,rgba(255,255,255,0.5),transparent_25%)]" />

      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        className="relative mx-auto max-w-[1275px]"
      >
        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="mb-7 max-w-[480px] lg:mb-6"
        >
          <div className="inline-flex items-center rounded-full border border-[#cfcfcf] bg-[#f7f7f6] px-3 py-1 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset]">
            <span className="font-host text-[12px] font-medium leading-none tracking-[-0.01em] text-[#ff7a29]">
              Private Van Tours
            </span>
          </div>

          <h2 className="font-host text-[24px] font-semibold leading-[1.25] tracking-[-0.02em] text-[#3C3838] lg:text-[30px]">
            Multi-Day European Adventures
          </h2>

          <p className="mt-3 max-w-[520px] font-host text-[16px] font-thin leading-[1.45] tracking-[-0.03em] text-[#8A8686] mb-12">
            Travel across multiple destinations with a private driver, premium vehicle,
            and a flexible itinerary designed around your journey.
          </p>
        </motion.div>

        <div className="relative">
          <motion.div
            ref={scrollRef}
            variants={{
              hidden: { opacity: 0, x: 30 },
              visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
            }}
            className="flex gap-[11px] overflow-x-auto snap-x snap-mandatory pb-4 pr-10 scrollbar-hide lg:gap-[12px]"
          >
            {vanTours.map((tour) => (
              <TourCard
                key={tour.id}
                id={tour.id}
                title={tour.title}
                subtitle={tour.subtitle}
                duration={tour.duration}
                price={tour.price}
                image={tour.image}
                isSaved={savedIds.includes(String(tour.id))}
                onSaveToggle={() =>
                  toggleSaved({
                    id: tour.id,
                    title: tour.title,
                    duration: tour.duration,
                    price: tour.price,
                    image: tour.image,
                  })
                }
              />
            ))}
          </motion.div>

          <button
            type="button"
            onClick={scroll}
            aria-label="Scroll tours"
            className="absolute right-[-16px] top-1/2 hidden h-[49px] w-[49px] -translate-y-[2px] items-center justify-center rounded-full bg-[#c9df68] shadow-[0_2px_5px_rgba(0,0,0,0.12)] transition-all duration-200 hover:-translate-y-1 hover:bg-[#b8d255] hover:shadow-md active:scale-95 lg:flex"
          >
            <ArrowRight className="h-[22px] w-[22px] stroke-[2.4] text-[#111111]" />
          </button>
        </div>

        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="mt-12 flex items-center justify-center gap-3 lg:mt-14"
        >
          <Link
            to="/private-tours"
            className="flex h-[57px] items-center justify-center rounded-full border border-[#cdcdcd] bg-[#f3f3f1] px-10 font-host text-[18px] font-normal tracking-[-0.02em] text-[#ff7321] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#ff7321] hover:bg-white hover:shadow-md active:scale-[0.98] lg:min-w-[286px]"
          >
            Explore All Van Tours
          </Link>

          <Link
            to="/private-tours"
            aria-label="Explore all van tours"
            className="grid h-[58px] w-[58px] place-items-center rounded-full bg-[#121212] shadow-[0_3px_10px_rgba(0,0,0,0.18)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2a2a2a] active:scale-[0.96]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 17L17 7"
                stroke="#FFFFFF"
                strokeWidth="2.35"
                strokeLinecap="square"
              />
              <path
                d="M8 7H17V16"
                stroke="#FFFFFF"
                strokeWidth="2.35"
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
            </svg>
          </Link>
        </motion.div>
      </motion.div>
    </section>
  )
}
